import {
  TrendingUp,
  Target,
  PhoneCall,
  CalendarCheck,
  Map,
  Megaphone,
  BarChart3,
  Bot,
  Globe2,
  HeartPulse,
  Server,
  Factory,
  Landmark,
  Truck,
  Sparkles,
  GraduationCap,
  Building2,
  Plane,
  ShoppingBag,
  Zap,
  Clapperboard,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "sdr-bdr",
    title: "SDR & BDR Services",
    icon: Target,
    short: "Outsourced sales development teams that build pipeline at scale.",
    long: "Dedicated SDRs and BDRs trained on your ICP, value prop, and objection handling. We deliver consistent outbound output across email, calls, and LinkedIn — measured by meetings booked and pipeline created.",
    deliverables: ["Trained SDR/BDR pods", "Multi-channel outbound", "CRM hygiene & reporting", "Weekly QBR cadence"],
  },
  {
    slug: "lead-generation",
    title: "Lead Generation",
    icon: TrendingUp,
    short: "Highly targeted, intent-driven lead generation engines.",
    long: "Account selection, signal-based prospecting, and enrichment built around your ICP. We blend data, automation, and human research to surface qualified buyers actively in-market.",
    deliverables: ["ICP & TAM modeling", "Intent + firmographic targeting", "Verified contact lists", "Lead scoring frameworks"],
  },
  {
    slug: "cold-calling",
    title: "Cold Calling",
    icon: PhoneCall,
    short: "Human-led calling programs that open doors with decision-makers.",
    long: "Native and accent-neutral callers trained on your industry, geography, and persona. We combine call cadences with multi-touch follow-ups to maximize connect rates and meeting bookings.",
    deliverables: ["Persona-trained callers", "Daily call quotas", "Call analytics & QA", "Recorded coaching loops"],
  },
  {
    slug: "appointment-setting",
    title: "Appointment Setting",
    icon: CalendarCheck,
    short: "Qualified meetings delivered directly to your AEs' calendars.",
    long: "We don't just book meetings — we book the right meetings. BANT/MEDDIC qualified appointments with confirmed budget, authority, need, and timeline.",
    deliverables: ["BANT-qualified meetings", "No-show recovery", "Direct calendar integration", "Meeting briefs for AEs"],
  },
  {
    slug: "gtm-strategy",
    title: "GTM Strategy",
    icon: Map,
    short: "Go-to-market playbooks engineered for repeatable revenue.",
    long: "From positioning and segmentation to channel mix and pricing, we co-build the GTM motion that turns your product into a category contender. Backed by data, validated in market.",
    deliverables: ["Positioning & messaging", "Segmentation & ICP", "Channel & pricing strategy", "Launch playbooks"],
  },
  {
    slug: "performance-marketing",
    title: "Performance Marketing",
    icon: Megaphone,
    short: "Full-funnel paid programs tied to pipeline, not vanity metrics.",
    long: "Search, social, ABM, and retargeting campaigns that drive qualified pipeline. We optimize for CAC, payback, and revenue — never just clicks.",
    deliverables: ["Paid search & social", "ABM orchestration", "Landing page CRO", "Attribution modeling"],
  },
  {
    slug: "revenue-operations",
    title: "Revenue Operations",
    icon: BarChart3,
    short: "Unify sales, marketing, and CS under one revenue engine.",
    long: "We design and operate the systems, data models, and dashboards that align every revenue function. Forecasts you trust, pipeline you can see, and a stack that compounds.",
    deliverables: ["RevOps audit & roadmap", "CRM & MAP architecture", "Pipeline & forecast hygiene", "Executive dashboards"],
  },
  {
    slug: "ai-sales-automation",
    title: "AI Sales Automation",
    icon: Bot,
    short: "AI agents and workflows that scale sales productivity 10x.",
    long: "Agentic AI for research, personalization, follow-ups, meeting prep, and CRM updates. Free your reps to do what humans do best — close.",
    deliverables: ["AI SDR assistants", "Auto-personalized sequences", "Conversation intelligence", "Workflow automation"],
  },
  {
    slug: "global-market-expansion",
    title: "Global Market Expansion",
    icon: Globe2,
    short: "Enter new geographies without standing up local sales orgs.",
    long: "US, EMEA, APAC, and ANZ market entry programs — covering local market research, regulatory considerations, native-speaking sales pods, and partnership channels.",
    deliverables: ["Market sizing & entry plan", "Localized sales teams", "Channel & partner activation", "Compliance & in-market ops"],
  },
];

export const INDUSTRIES = [
  { slug: "it-saas", title: "IT — Products, Services & SaaS", icon: Server, blurb: "Custom dev, managed services, cloud, data engineering, and horizontal/vertical SaaS." },
  { slug: "ai-companies", title: "AI Companies", icon: Sparkles, blurb: "Foundation models, AI infra, applied AI for enterprise." },
  { slug: "bfsi", title: "BFSI", icon: Landmark, blurb: "Banking, financial services, and insurance. Compliance-aware revenue programs across retail, commercial, and digital." },
  { slug: "healthcare", title: "Healthcare", icon: HeartPulse, blurb: "Provider networks, payor solutions, healthtech SaaS, RCM." },
  { slug: "manufacturing", title: "Manufacturing", icon: Factory, blurb: "Industrial OEMs, MRO, Industry 4.0 platforms, distributors." },
  { slug: "logistics", title: "Transportation & Logistics", icon: Truck, blurb: "3PL, freight tech, supply chain visibility, last-mile." },
  { slug: "education", title: "Education & EdTech", icon: GraduationCap, blurb: "K-12, higher-ed, workforce learning, LMS platforms, and online learning providers." },
  { slug: "real-estate", title: "Real Estate & PropTech", icon: Building2, blurb: "Commercial & residential brokerages, REITs, property management SaaS, construction tech." },
  { slug: "aviation", title: "Aviation & Aerospace", icon: Plane, blurb: "Airlines, MRO, aerospace OEMs, airport operators, and aviation technology providers." },
  { slug: "retail-ecom", title: "Retail & E-commerce", icon: ShoppingBag, blurb: "D2C brands, omnichannel retailers, marketplaces, and commerce enablement platforms." },
  { slug: "energy-utilities", title: "Energy & Utilities", icon: Zap, blurb: "Clean energy, oil & gas, utilities tech, grid modernization, and ESG platforms." },
  { slug: "media-entertainment", title: "Media & Entertainment", icon: Clapperboard, blurb: "Streaming, publishing, content tech, adtech, and creator-economy platforms." },
];

export const METRICS = [
  { value: "2X", label: "Revenue Growth", desc: "Faster top-line acceleration in 12 months." },
  { value: "4X", label: "Cost Optimization", desc: "Lower fully-loaded cost vs in-house SDR." },
  { value: "8X", label: "Healthier Qualified Pipeline", desc: "More BANT-qualified pipeline coverage." },
  { value: "2X", label: "Geographic Expansion", desc: "New markets entered with native pods." },
];

export const CASE_STUDIES = [
  {
    slug: "healthcare-rcm-nemt-us-gtm",
    industry: "Healthcare",
    title: "AI-powered healthcare SaaS accelerates US customer acquisition",
    summary:
      "A US healthcare technology company offering AI-powered RCM and NEMT solutions needed to engage decision-makers and build a predictable sales pipeline. Sozos delivered a structured GTM engine aligned to the healthcare buyer.",
    impact: ["Decision-maker conversations opened", "Qualified opportunities created", "New accounts acquired"],
    image:
      "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    featured: true,
    geography: "United States",
    engagement: "Strategic GTM, Lead Generation & Business Development",
    duration: "Ongoing",
    detail: {
      challenge:
        "A US-focused healthcare technology company offering AI-powered Revenue Cycle Management (RCM) and Non-Emergency Medical Transportation (NEMT) solutions needed to accelerate customer acquisition in a highly competitive market. The company faced challenges identifying qualified buyers, engaging healthcare decision-makers, and building a predictable sales pipeline.",
      approach: [
        "Industry-specific market research and ICP development",
        "Targeted outreach to healthcare providers, transportation operators, and business owners",
        "Multichannel prospecting through personalized emails, cold calling, and LinkedIn",
        "Decision-maker engagement, lead qualification, and appointment setting",
        "Sales pipeline management and opportunity development",
      ],
      outcomesIntro:
        "Sozos helped establish a structured customer acquisition engine, generating meaningful conversations with healthcare decision-makers and converting qualified prospects into new business accounts.",
      impactStatement:
        "Sozos enabled the client to accelerate market penetration, strengthen its US sales pipeline, and establish a repeatable business development process.",
      tagline: "From Market Entry to Customer Acquisition — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "ai-enterprise-automation-logistics",
    industry: "AI",
    title: "AI enterprise automation platform cracks US logistics",
    summary:
      "An AI-powered automation platform needed access to C-suite leaders in logistics, transportation, and supply chain. Sozos designed a focused enterprise sales motion that opened doors with CEOs, COOs, CIOs and technology decision-makers.",
    impact: ["Senior executive conversations opened", "Qualified pipeline developed", "New enterprise logos secured"],
    image:
      "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    featured: true,
    geography: "United States",
    engagement: "GTM Strategy, Enterprise Prospecting & Business Development",
    duration: null,
    detail: {
      challenge:
        "An emerging AI-powered enterprise automation platform sought to expand its presence across the US logistics, transportation, and supply chain industries. Despite offering innovative automation capabilities, the company needed access to senior decision-makers — including CEOs, COOs, CIOs, and technology leaders — to accelerate enterprise adoption.",
      approach: [
        "Identified and segmented target accounts across logistics, transportation, 3PL, and warehousing",
        "Developed industry-specific value propositions emphasizing AI automation, operational efficiency, and cost optimization",
        "Engaged C-suite executives and technology decision-makers",
        "Executed personalized outreach through email, cold calling, and LinkedIn",
        "Facilitated discovery meetings, product demonstrations, and commercial discussions",
        "Built and managed a qualified enterprise sales pipeline",
      ],
      outcomesIntro:
        "Sozos successfully established conversations with relevant enterprise decision-makers, creating qualified sales opportunities and supporting customer acquisition in the competitive US logistics technology market.",
      impactStatement:
        "The engagement helped the client accelerate enterprise market entry, strengthen its sales pipeline, and establish a scalable customer acquisition framework.",
      tagline: "Turning AI Innovation into Enterprise Sales Opportunities — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "bfsi-enterprise-gtm-global",
    industry: "BFSI",
    title: "BFSI technology provider expands across global financial markets",
    summary:
      "A technology provider specialising in banking tech, cybersecurity, cloud transformation, and enterprise applications needed to engage senior banking executives across North America, EMEA & APAC. Sozos built a strategic enterprise sales framework around CIOs, CTOs, CISOs and digital transformation leaders.",
    impact: ["Executive relationships established", "Enterprise opportunities created", "New accounts initiated"],
    image:
      "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "North America, EMEA & APAC",
    engagement: "Enterprise GTM, Strategic Account Development & Sales Pipeline Generation",
    duration: null,
    detail: {
      challenge:
        "A technology solutions provider specializing in banking technology, cybersecurity, cloud transformation, and enterprise applications wanted to expand its presence across international financial services markets. The primary challenge was engaging senior banking and financial services executives, navigating complex enterprise buying cycles, and generating qualified opportunities for high-value technology engagements.",
      approach: [
        "Identifying banks, financial institutions, NBFCs, fintech companies, and insurance providers",
        "Targeting CIOs, CTOs, CISOs, IT Directors, and digital transformation leaders",
        "Developing account-based marketing and enterprise prospecting strategies",
        "Positioning cybersecurity, AI, cloud, and digital transformation solutions around business outcomes",
        "Engaging senior stakeholders through personalized multichannel outreach",
        "Supporting opportunity qualification, solution discussions, and enterprise sales progression",
      ],
      outcomesIntro:
        "The engagement established qualified conversations with senior banking and financial services decision-makers and surfaced enterprise opportunities across target markets.",
      impactStatement:
        "A focused enterprise GTM strategy can help BFSI technology providers establish stronger relationships with financial institutions, improve access to senior decision-makers, and develop a sustainable pipeline for high-value technology engagements.",
      tagline: "Connecting Technology Innovation with Enterprise Banking Opportunities — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "saas-enterprise-pipeline",
    industry: "SaaS",
    title: "Mid-market SaaS scales enterprise pipeline",
    summary: "A B2B SaaS team unlocked enterprise conversations through a dedicated SDR pod and AI-personalized outbound.",
    impact: ["Enterprise meetings secured", "Qualified pipeline created", "Repeatable motion built"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "North America",
    engagement: "Outbound SDR Program & Enterprise Pipeline Build",
    duration: "Program-based",
    detail: {
      challenge:
        "A mid-market B2B SaaS company had a strong product-market fit with SMB customers but struggled to break into enterprise accounts. Founder-led selling capped at the top of the funnel, and the in-house SDR team was stretched thin across inbound follow-up and events. Enterprise pipeline coverage was insufficient and win rates against incumbents were slipping.",
      approach: [
        "Defined a tight enterprise ICP with specific firmographic and tech-stack triggers",
        "Deployed a dedicated Sozos pod focused exclusively on named enterprise accounts",
        "Built AI-personalized outbound sequences grounded in prospect-specific pain points",
        "Introduced a weekly deal-review cadence between the Sozos pod and the client AE team",
        "Instrumented pipeline hygiene with stage definitions, exit criteria, and conversion dashboards",
      ],
      outcomesIntro:
        "Enterprise pipeline coverage expanded materially and the client closed its first strategic enterprise deals from the targeted account list.",
      impactStatement:
        "The engagement transformed enterprise motion from reactive founder-led selling to a repeatable, metrics-driven pipeline engine the client could scale.",
      tagline: "From Founder-Led Selling to a Repeatable Enterprise Motion — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "healthcare-rcm-expansion",
    industry: "Healthcare",
    title: "Healthcare provider enters new US regions",
    summary: "Targeted outbound and account-based outreach opened conversations with physician groups and hospital systems.",
    impact: ["Physician-group conversations opened", "Health-system opportunities surfaced", "New regions entered"],
    image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "United States (Midwest & Southeast expansion)",
    engagement: "Geographic Expansion GTM & Account-Based Outreach",
    duration: "Program-based",
    detail: {
      challenge:
        "A regional healthcare services provider was concentrated in a handful of coastal states and wanted to expand into the Midwest and Southeast. The internal BD team had no local relationships, limited understanding of regional payer dynamics, and no repeatable playbook for engaging physician groups or hospital system procurement.",
      approach: [
        "Built a prioritized target list of physician groups and hospital systems across the new target states",
        "Developed region-specific messaging aligned to each state's payer mix and reimbursement realities",
        "Executed multichannel outreach — personalized email, direct dial, and LinkedIn — to CMOs, CFOs, and VPs of Operations",
        "Facilitated discovery calls with qualified decision-makers and coordinated clinical-leadership demos",
        "Transferred relationship ownership to the client's regional account executives at the opportunity stage",
      ],
      outcomesIntro:
        "The program opened the door to qualified physician-group conversations and surfaced active health-system opportunities across the target geographies.",
      impactStatement:
        "The client successfully entered new regions with validated demand and a repeatable account-based motion that no longer relied on inbound referrals.",
      tagline: "Validated Geographic Expansion Without Burning the Internal Team — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "ai-company-go-to-market",
    industry: "AI",
    title: "Applied AI startup builds a repeatable GTM motion",
    summary: "Sharper positioning and AI-augmented outbound took the founder out of every first meeting.",
    impact: ["Enterprise discovery calls secured", "Paid pilots initiated", "Founder decoupled from first calls"],
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "North America & EMEA",
    engagement: "Positioning, Outbound Build-out & Founder Decoupling",
    duration: "Program-based",
    detail: {
      challenge:
        "A Series-A applied AI startup had strong technical founder-led wins but a messaging crisis — multiple different pitches depending on which founder was in the room. Enterprise meetings all required the technical co-founder, which capped scale. The team needed a GTM motion that didn't require the founder on every first call.",
      approach: [
        "Ran a positioning sprint to converge on one crisp enterprise value proposition and vertical-specific cuts",
        "Rebuilt outbound sequences with AI-augmented personalization grounded in each prospect's business metrics",
        "Trained a dedicated Sozos pod on the technical story to run first-call discovery independently",
        "Introduced a tiered meeting playbook: SDR qualifier → AE discovery → founder-led technical deep-dive",
        "Instrumented a weekly pipeline review covering message-to-meeting conversion by vertical",
      ],
      outcomesIntro:
        "The founder's calendar reclaimed meaningful first-meeting hours while the enterprise pipeline grew and the first paid pilots signed quickly.",
      impactStatement:
        "The engagement decoupled the founder from the sales cycle and gave the company a scalable GTM motion ready for the Series-B growth story.",
      tagline: "A GTM Motion That Scales Beyond the Founder's Calendar — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "manufacturing-distributor",
    industry: "Manufacturing",
    title: "Industrial OEM opens distributor partnerships",
    summary: "Account-based outbound and channel activation reached major distributors and industrial buyers across North America.",
    impact: ["Distributor conversations opened", "Strategic distributor wins", "Named-account programs launched"],
    image: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "North America (US, Canada, Mexico)",
    engagement: "Channel Activation & Named-Account Outreach",
    duration: "Program-based",
    detail: {
      challenge:
        "A European industrial OEM wanted to grow its North American footprint but had minimal brand recognition and no established distributor network outside the Northeast. The internal team was split between direct sales and channel development with no capacity to pursue new partnerships systematically.",
      approach: [
        "Built a prioritized distributor map covering target partners across industrial sub-verticals",
        "Developed partner-first messaging emphasizing margin economics, local territory protection, and training support",
        "Executed outreach to GMs, VP Sales, and category managers at target distributors",
        "Coordinated partner evaluation calls, product demos, and pricing/territory discussions",
        "Supported a parallel named-account motion to seed demand with large end-customers",
      ],
      outcomesIntro:
        "The engagement signed new strategic distributor agreements and launched co-selling programs with named enterprise accounts.",
      impactStatement:
        "The engagement expanded the North American footprint from a single-region presence to a tri-country distributor network with validated end-customer demand already in motion.",
      tagline: "From One Region to a Continent — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "bfsi-commercial-bank-pipeline",
    industry: "BFSI",
    title: "Commercial bank rebuilds SME outreach",
    summary: "A compliant, sector-segmented outbound motion helped engage SME treasury and lending prospects across regions.",
    impact: ["Treasury conversations opened", "Qualified lending opportunities", "Compliance-aligned motion"],
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "North America",
    engagement: "Compliance-Aware SME Outbound & Relationship Development",
    duration: "Program-based",
    detail: {
      challenge:
        "A commercial bank had a mandate to grow SME lending but relied on branch walk-ins and legacy referral networks. Previous attempts at outbound had been shut down due to compliance concerns around unapproved messaging. The leadership team needed a disciplined, compliance-friendly motion that could generate pipeline at scale.",
      approach: [
        "Partnered with the bank's compliance team to co-design pre-approved messaging and disclaimer language",
        "Segmented SMEs by sector, revenue band, and primary banking relationship",
        "Built sector-specific outbound sequences for manufacturing, professional services, and healthcare SMEs",
        "Trained the Sozos pod on bank-specific compliance protocols including call-recording and consent capture",
        "Instrumented a monthly audit loop where compliance reviewed sampled outreach for ongoing certification",
      ],
      outcomesIntro:
        "The program generated qualified treasury and lending conversations with a clean compliance record throughout the engagement.",
      impactStatement:
        "The bank gained its first repeatable, regulator-friendly outbound engine — proving that disciplined compliance and outbound growth can coexist.",
      tagline: "Growth Inside the Guardrails — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "bfsi-fintech-acquisition",
    industry: "BFSI",
    title: "Fintech engages enterprise insurers",
    summary: "A multi-threaded ABM motion helped a specialty fintech reach buying committees inside top-tier insurance carriers.",
    impact: ["Enterprise insurer meetings", "Proof-of-concept engagements", "Enterprise contracts signed"],
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "United States & United Kingdom",
    engagement: "Account-Based Marketing & Enterprise Insurer Outreach",
    duration: "Program-based",
    detail: {
      challenge:
        "A specialty fintech selling claims-automation software to insurance carriers had closed early mid-market deals but could not break into top-tier carriers. The buying process required engagement across claims, IT, procurement, and risk — a multi-threaded motion the company had never run before.",
      approach: [
        "Built a tier-1 target account list of top-tier insurance carriers across US and UK",
        "Mapped buying committees for each account (claims operations, IT architecture, procurement, compliance)",
        "Executed role-specific messaging and ABM campaigns targeting multiple stakeholders per account",
        "Coordinated executive briefings, technical deep-dives, and procurement discussions in parallel tracks",
        "Supported commercial negotiation and multi-stakeholder deal progression",
      ],
      outcomesIntro:
        "The multi-threaded ABM motion unlocked buying committees inside top-tier carriers and converted several into paid proof-of-concept engagements.",
      impactStatement:
        "The engagement proved the fintech could sell into the largest insurance carriers and materially compressed the typical enterprise cycle.",
      tagline: "Breaking Into the Top-20 — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "it-services-us-expansion",
    industry: "IT Services",
    title: "IT services firm enters North America",
    summary: "A dedicated US-aligned SDR pod and CXO-focused ABM opened enterprise relationships across BFSI and Manufacturing buyers.",
    impact: ["CXO relationships established", "Qualified enterprise opportunities", "First North America logos"],
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "United States & Canada",
    engagement: "North America Market Entry & CXO-Level ABM",
    duration: "Program-based",
    detail: {
      challenge:
        "A mid-sized IT services firm with strong delivery credentials across EMEA and APAC wanted to establish its first North America logos. The leadership had no local sales presence, limited brand recognition outside of specific analyst reports, and a very targeted ICP: BFSI and Manufacturing enterprise buyers with specific technology-modernization budgets.",
      approach: [
        "Established a US-time-zone-aligned SDR pod with industry-specialist research support",
        "Positioned the firm's EMEA/APAC delivery proof-points as differentiators for cost-sensitive US buyers",
        "Executed CXO-level ABM targeting CIOs, CTOs, and heads of digital transformation across named accounts",
        "Coordinated executive briefings, analyst-report mentions, and reference calls with existing EMEA customers",
        "Supported MSA negotiation and the complex procurement onboarding typical in BFSI and Manufacturing",
      ],
      outcomesIntro:
        "The firm secured its first North America logos and built a qualified pipeline covering its new-market revenue target.",
      impactStatement:
        "The engagement delivered a validated North America market-entry framework and reference logos the firm now uses as proof-points for all future US expansion.",
      tagline: "First North America Logos — Powered by Sozos Global Services.",
    },
  },
  {
    slug: "logistics-3pl-growth",
    industry: "Transportation & Logistics",
    title: "3PL provider escapes commodity bidding",
    summary: "A vertical-specific outbound motion focused on retail and CPG shippers helped land long-term contracts beyond RFPs.",
    impact: ["Mid-market shipper conversations", "Long-term contracts signed", "Pre-RFP positioning secured"],
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200",
    featured: true,
    geography: "United States (Retail & CPG verticals)",
    engagement: "Vertical-Specific Outbound & Direct-Shipper Development",
    duration: "Program-based",
    detail: {
      challenge:
        "A regional 3PL was trapped in commodity RFP cycles — competing on price every quarter with razor-thin margins. The leadership team wanted to escape price-only bidding by developing direct relationships with mid-market retail and CPG shippers before RFPs went out, but internal reps were saturated managing existing accounts.",
      approach: [
        "Built a target list of mid-market retail and CPG shippers across priority revenue bands",
        "Developed vertical-specific messaging around service-level consistency, dedicated fleet, and seasonal flex capacity",
        "Executed multichannel outreach to VP Supply Chain, Logistics Managers, and Procurement Directors",
        "Positioned Sozos pod conversations ahead of annual RFP cycles — before pricing became the only lever",
        "Supported commercial negotiation and multi-year dedicated-capacity contract discussions",
      ],
      outcomesIntro:
        "The 3PL signed multi-year dedicated-capacity contracts outside the usual RFP cycle, at a materially higher average contract value than its historical mix.",
      impactStatement:
        "The engagement transformed the client from a commodity bidder into a strategic logistics partner, with contracts locked in before competitors ever saw the RFP.",
      tagline: "Winning Before the RFP — Powered by Sozos Global Services.",
    },
  },
];

export const SERVICE_OPTIONS = SERVICES.map((s) => s.title);
export const INDUSTRY_OPTIONS = INDUSTRIES.map((i) => i.title);

export const PACKAGES = [
  {
    slug: "growth-launch",
    name: "Growth Launch",
    price: 3000,
    tagline: "Startups & early-stage companies entering new markets",
    team: ["Dedicated SDRs", "Performance Marketing Specialists"],
    deliverables: [
      "Prospect database building",
      "Outbound calling",
      "Email campaigns",
      "LinkedIn outreach",
      "Meeting scheduling",
      "Monthly reporting",
    ],
    highlight: false,
    ribbon: "Launch",
  },
  {
    slug: "growth-accelerator",
    name: "Growth Accelerator",
    price: 7000,
    tagline: "Scale pipeline generation and outbound outreach",
    team: ["Dedicated SDRs", "Performance Marketing Specialists"],
    deliverables: [
      "Multi-channel outbound campaigns",
      "Account-based outreach",
      "Lead qualification",
      "Pipeline management",
      "Paid campaign management",
      "Weekly performance reviews",
    ],
    highlight: true,
    ribbon: "Most popular",
  },
  {
    slug: "growth-enterprise",
    name: "Growth Enterprise",
    price: 15000,
    tagline: "Mid-market & enterprise targeting aggressive revenue growth",
    team: ["Dedicated SDRs", "Performance Marketing Specialists"],
    deliverables: [
      "Dedicated SDR pod structure",
      "Multi-market expansion",
      "High-volume prospecting",
      "Enterprise account targeting",
      "Performance marketing campaigns",
      "Executive reporting & analytics",
      "GTM optimization support",
    ],
    highlight: false,
    ribbon: "Enterprise",
  },
];

export const INCLUDED_ACROSS_ALL = [
  "SDR Recruitment & Management",
  "SDR Training & Coaching",
  "Outbound Calling & Email Outreach",
  "LinkedIn Prospecting",
  "Lead Qualification",
  "Appointment Setting",
  "Weekly Reporting",
  "Monthly Business Reviews",
];

export const ADD_ONS = [
  "Dedicated SDR Manager",
  "Revenue Operations Support",
  "CRM Administration",
  "GTM Consulting",
  "Website & Landing Page Development",
  "AI Revenue Agents",
  "Additional Performance Marketing Resources",
];
